import type { Mass } from './mass.ts';
import { floorToLoadable, nextLoadableAbove, type Gym } from './plates.ts';

/** Reason codes shown on every target (UI spec C6). */
export type ReasonCode =
  | 'FIRST'
  | 'INC_SUCCESS'
  | 'ROUNDED'
  | 'ROUNDED_UP'
  | 'NEED_SMALLER_PLATES'
  | 'REPEAT_MISS'
  | 'REPEAT_SKIPPED'
  /** ADR-0025: the lift was lightened mid-workout, so it holds its weight (no step up, no miss). */
  | 'REPEAT_LIGHTER'
  | 'DELOAD_OFFER'
  | 'DELOAD_DONE'
  | 'DELOAD_DECLINED'
  | 'RETURN_BREAK'
  | 'MANUAL';

export interface LiftRule {
  /** Normal increment after a successful session. */
  increment: Mass;
  /** Increment once the lift has missed at least once (Deadlift 5 kg → 2.5 kg, ADR-0008). Defaults to `increment`. */
  incrementAfterFirstMiss?: Mass;
  /** Consecutive misses that trigger a deload offer (official default 3). */
  deloadAfter: number;
  /** Deload fraction (official default 0.10). */
  deloadFraction: number;
  /**
   * Opt-in sets × reps after stalling (ADR-0019): 5×5 by default (1×5 deadlift). The archived StrongLifts guides
   * describe 3×5 then 3×3; the switch is always the user's choice, never automatic.
   */
  scheme?: { sets: number; reps: number };
}

export interface LiftState {
  /** Weight prescribed for the session just finished. */
  weight: Mass;
  /** Consecutive misses of this exercise. */
  misses: number;
  /** Has this lift ever missed? Drives `incrementAfterFirstMiss`. */
  hasMissed: boolean;
}

export type SessionOutcome = 'success' | 'miss' | 'skipped';

export interface DeloadOffer {
  to: Mass;
  /** Actual reduction after rounding to a loadable weight, e.g. 0.1 or 0.167. */
  fraction: number;
}

export interface Decision {
  next: LiftState;
  reason: ReasonCode;
  outcome: SessionOutcome;
  /** Present when the engine offers (never forces) a deload. */
  offer?: DeloadOffer;
}

/**
 * Classify one exercise's working sets (ADR-0008 + review M6). `logged[i]` is the reps achieved on set
 * i, or null if the set was not logged. Success needs EVERY prescribed set logged at or above target.
 * Warm-ups must never be passed in here.
 */
export function classify(logged: readonly (number | null)[], prescribedSets: number, targetReps: number): SessionOutcome {
  if (logged.length !== prescribedSets) throw new RangeError(`Expected ${prescribedSets} sets, got ${logged.length}`);
  if (logged.every((r) => r === null)) return 'skipped';
  return logged.every((r) => r !== null && r >= targetReps) ? 'success' : 'miss';
}

/** Deload target: floor to loadable, never below the bar; suppressed if it wouldn't change the weight. */
export function deloadOffer(gym: Gym, weight: Mass, fraction: number): DeloadOffer | undefined {
  const raw = Math.floor(weight * (1 - fraction));
  const to = Math.max(floorToLoadable(gym, raw) ?? gym.bar, gym.bar);
  if (to >= weight) return undefined;
  return { to, fraction: Math.round(((weight - to) / weight) * 1000) / 1000 };
}

/** The next target after one session of one lift. Pure; depends only on its inputs (ADR-0002 step-wise). */
export function decide(state: LiftState, rule: LiftRule, outcome: SessionOutcome, gym: Gym): Decision {
  if (outcome === 'skipped') {
    return { next: state, reason: 'REPEAT_SKIPPED', outcome };
  }

  if (outcome === 'miss') {
    const misses = state.misses + 1;
    const next: LiftState = { weight: state.weight, misses, hasMissed: true };
    if (misses >= rule.deloadAfter) {
      const offer = deloadOffer(gym, state.weight, rule.deloadFraction);
      if (offer) return { next, reason: 'DELOAD_OFFER', outcome, offer };
    }
    return { next, reason: 'REPEAT_MISS', outcome };
  }

  const inc = state.hasMissed ? (rule.incrementAfterFirstMiss ?? rule.increment) : rule.increment;
  const wanted = state.weight + inc;
  const floored = floorToLoadable(gym, wanted);
  const base = { misses: 0, hasMissed: state.hasMissed };
  if (floored !== null && floored > state.weight) {
    return { next: { ...base, weight: floored }, reason: floored === wanted ? 'INC_SUCCESS' : 'ROUNDED', outcome };
  }
  // Rounding down would stall progress forever (review B2): round up to the next loadable weight instead.
  const up = nextLoadableAbove(gym, state.weight);
  if (up !== null) return { next: { ...base, weight: up }, reason: 'ROUNDED_UP', outcome };
  return { next: { ...base, weight: state.weight }, reason: 'NEED_SMALLER_PLATES', outcome };
}

/** User accepted a deload offer: drop the weight and reset the miss counter. */
export function acceptDeload(state: LiftState, offer: DeloadOffer): { next: LiftState; reason: ReasonCode } {
  return { next: { weight: offer.to, misses: 0, hasMissed: state.hasMissed }, reason: 'DELOAD_DONE' };
}

/** User declined: keep the weight; the counter stays, so the offer returns after the next miss. */
export function declineDeload(state: LiftState): { next: LiftState; reason: ReasonCode } {
  return { next: state, reason: 'DELOAD_DECLINED' };
}

/** User set the weight by hand: the miss counter resets (ADR-0008). */
export function overrideWeight(state: LiftState, weight: Mass): { next: LiftState; reason: ReasonCode } {
  return { next: { weight, misses: 0, hasMissed: state.hasMissed }, reason: 'MANUAL' };
}
